import { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { StatusBadge, formatDate, formatDateTime } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  getAllUsers, createUser, updateUser, deleteUser, setUserAvailability,
  getAllRequests, getDashboardStats, getAuditLogs, getCourses, adminResetPassword,
  adminForwardToVP, adminForwardCoordinatorPass
} from "../lib/api";
import { 
  Users, FileText, Clock, CheckCircle2, XCircle, ShieldCheck, 
  Plus, Pencil, Trash2, Loader2, BarChart3, History, Settings,
  UserCog, AlertCircle, Bell, GraduationCap, KeyRound, ArrowRight
} from "lucide-react";
import NotificationSettings from "./NotificationSettings";
import StudentManagement from "./StudentManagement";
import ReportsModule from "./ReportsModule";

const ROLES = [
  { value: "student", label: "Student" },
  { value: "reception", label: "Reception" },
  { value: "coordinator", label: "Class Coordinator" },
  { value: "principal", label: "Principal" },
  { value: "vice_principal", label: "Vice Principal" },
  { value: "hostel_warden", label: "Hostel Warden" },
  { value: "gate_operator", label: "Gate Operator" },
  { value: "admin", label: "Administrator" },
];

const AdminDashboard = () => {
  return (
    <DashboardLayout title="Admin Portal">
      <Routes>
        <Route index element={<AdminHome />} />
      </Routes>
    </DashboardLayout>
  );
};

const AdminHome = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // User management
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: "", password: "", name: "", role: "student", phone: "",
    enrollment_number: "", course_id: "", year: "", hostel_status: "hostel",
    parent_name: "", parent_phone: "", assigned_course_id: "", assigned_year: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwValue, setResetPwValue] = useState("");
  const [resetPwLoading, setResetPwLoading] = useState(false);

  const handleResetPassword = async () => {
    if (!resetPwValue || resetPwValue.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    setResetPwLoading(true);
    try {
      const { data } = await adminResetPassword({ user_id: resetPwUser.id, new_password: resetPwValue });
      toast.success(data.message);
      setResetPwUser(null);
      setResetPwValue("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetPwLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [statsRes, usersRes, requestsRes, logsRes, coursesRes] = await Promise.all([
        getDashboardStats(),
        getAllUsers(),
        getAllRequests({}),
        getAuditLogs(50),
        getCourses()
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setRequests(requestsRes.data);
      setAuditLogs(logsRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!userFormData.email || !userFormData.name) {
      toast.error("Email and name are required");
      return;
    }
    if (!editingUser && !userFormData.password) {
      toast.error("Password is required for new users");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...userFormData,
        year: userFormData.year ? parseInt(userFormData.year) : null,
        assigned_year: userFormData.assigned_year ? parseInt(userFormData.assigned_year) : null,
      };
      
      if (editingUser) {
        delete payload.password;
        await updateUser(editingUser.id, payload);
        toast.success("User updated successfully");
      } else {
        await createUser(payload);
        toast.success("User created successfully");
      }
      
      setShowUserDialog(false);
      setEditingUser(null);
      resetUserForm();
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    try {
      await deleteUser(userId);
      toast.success("User deleted");
      loadAllData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleAvailabilityToggle = async (userId, currentStatus) => {
    try {
      await setUserAvailability(userId, !currentStatus);
      toast.success(`User marked as ${!currentStatus ? "available" : "unavailable"}`);
      loadAllData();
    } catch (error) {
      toast.error("Failed to update availability");
    }
  };

  const handleForwardToVP = async (passId, requestNumber) => {
    if (!window.confirm(`Forward request ${requestNumber} to Vice Principal? This will skip the Principal step.`)) return;
    try {
      await adminForwardToVP(passId);
      toast.success(`Request ${requestNumber} forwarded to Vice Principal!`);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to forward");
    }
  };

  const handleForwardCoordinatorPass = async (passId, requestNumber, target) => {
    const labels = { principal: "Principal", vp: "Vice Principal" };
    if (!window.confirm(`Forward request ${requestNumber} to ${labels[target] || target}? This will skip the Coordinator step.`)) return;
    try {
      await adminForwardCoordinatorPass(passId, target);
      toast.success(`Request ${requestNumber} forwarded to ${labels[target] || target}!`);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to forward");
    }
  };

  const resetUserForm = () => {
    setUserFormData({
      email: "", password: "", name: "", role: "student", phone: "",
      enrollment_number: "", course_id: "", year: "", hostel_status: "hostel",
      parent_name: "", parent_phone: "", assigned_course_id: "", assigned_year: ""
    });
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      email: user.email || "",
      password: "",
      name: user.name || "",
      role: user.role || "student",
      phone: user.phone || "",
      enrollment_number: user.enrollment_number || "",
      course_id: user.course_id || "",
      year: user.year?.toString() || "",
      hostel_status: user.hostel_status || "hostel",
      parent_name: user.parent_name || "",
      parent_phone: user.parent_phone || "",
      assigned_course_id: user.assigned_course_id || "",
      assigned_year: user.assigned_year?.toString() || ""
    });
    setShowUserDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="dashboard" data-testid="dashboard-tab" className="text-xs sm:text-sm">
            <BarChart3 className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Dashboard</span>
            <span className="sm:hidden">Home</span>
          </TabsTrigger>
          <TabsTrigger value="students" data-testid="students-tab" className="text-xs sm:text-sm">
            <GraduationCap className="h-4 w-4 mr-1 sm:mr-2" />
            Students
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="users-tab" className="text-xs sm:text-sm">
            <Users className="h-4 w-4 mr-1 sm:mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="requests-tab" className="text-xs sm:text-sm">
            <FileText className="h-4 w-4 mr-1 sm:mr-2" />
            Requests
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="reports-tab" className="text-xs sm:text-sm">
            <FileText className="h-4 w-4 mr-1 sm:mr-2" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="notifications-tab" className="text-xs sm:text-sm">
            <Bell className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Notifications</span>
            <span className="sm:hidden">Notify</span>
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="audit-tab" className="text-xs sm:text-sm">
            <History className="h-4 w-4 mr-1 sm:mr-2" />
            Audit
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Requests</p>
                      <p className="text-2xl font-semibold">{stats?.total || 0}</p>
                    </div>
                    <FileText className="h-8 w-8 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Pending</p>
                      <p className="text-2xl font-semibold text-amber-600">{stats?.pending || 0}</p>
                    </div>
                    <Clock className="h-8 w-8 text-amber-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Approved</p>
                      <p className="text-2xl font-semibold text-emerald-600">{stats?.approved || 0}</p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Rejected</p>
                      <p className="text-2xl font-semibold text-red-600">{stats?.rejected || 0}</p>
                    </div>
                    <XCircle className="h-8 w-8 text-red-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Gate Verified</p>
                      <p className="text-2xl font-semibold text-blue-600">{stats?.verified || 0}</p>
                    </div>
                    <ShieldCheck className="h-8 w-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Today's Stats */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Today's Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-8">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-slate-900">{stats?.today_requests || 0}</p>
                    <p className="text-sm text-slate-500">New Requests</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-emerald-600">{stats?.today_approved || 0}</p>
                    <p className="text-sm text-slate-500">Approved</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{stats?.today_verified || 0}</p>
                    <p className="text-sm text-slate-500">Gate Verified</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Requests by Course */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Requests by Course</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {stats?.by_course?.map((item) => {
                    const course = courses.find(c => c.id === item._id);
                    return (
                      <div key={item._id} className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500">{course?.name || item._id}</p>
                        <p className="text-2xl font-semibold">{item.count}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Staff Availability Control */}
            <Card className="border-amber-200 bg-amber-50/30" data-testid="staff-availability-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCog className="h-5 w-5 text-amber-600" />
                  Staff Availability
                </CardTitle>
                <CardDescription>
                  When a Coordinator or Principal is marked <strong>Unavailable</strong>, their approval step is automatically skipped and routed to the Vice Principal.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users
                    .filter(u => ["coordinator", "principal", "vice_principal"].includes(u.role))
                    .map(u => (
                      <div key={u.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200" data-testid={`staff-avail-${u.id}`}>
                        <div>
                          <p className="font-medium text-sm">{u.name}</p>
                          <p className="text-xs text-slate-500 capitalize">{u.role?.replace("_", " ")}
                            {u.role === "coordinator" && u.assigned_course_id && (
                              <span className="ml-1 text-blue-600">
                                ({courses.find(c => c.id === u.assigned_course_id)?.name} - Y{u.assigned_year})
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={u.is_available !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                            {u.is_available !== false ? "Available" : "Unavailable"}
                          </Badge>
                          <Switch
                            checked={u.is_available !== false}
                            onCheckedChange={() => handleAvailabilityToggle(u.id, u.is_available !== false)}
                            data-testid={`staff-avail-toggle-${u.id}`}
                          />
                        </div>
                      </div>
                    ))}
                  {users.filter(u => ["coordinator", "principal", "vice_principal"].includes(u.role)).length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">No coordinators or principals found. Create them in the Users tab.</p>
                  )}
                </div>
                <div className="mt-4 p-3 bg-amber-100 rounded-lg text-xs text-amber-800">
                  <p className="font-semibold mb-1">How it works:</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>If a <strong>Coordinator</strong> is unavailable, new pass requests for their course/year go directly to the Vice Principal (acting as Coordinator).</li>
                    <li>If the <strong>Principal</strong> is unavailable, approved requests skip Principal and go to the Vice Principal (acting as Principal).</li>
                    <li>The Vice Principal handles delegated approvals from both roles.</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Students Tab */}
        <TabsContent value="students">
          <StudentManagement />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>User Management</CardTitle>
                <CardDescription>Manage all system users and their roles</CardDescription>
              </div>
              <Button 
                onClick={() => { resetUserForm(); setEditingUser(null); setShowUserDialog(true); }}
                data-testid="add-user-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Mobile No.</TableHead>
                    <TableHead>Course/Year</TableHead>
                    <TableHead>Available</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {user.role?.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.phone ? (
                          <span className="text-sm">{user.phone}</span>
                        ) : (
                          <span className="text-xs text-red-400">No number</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.role === "student" && user.course_id && (
                          <span>{courses.find(c => c.id === user.course_id)?.name} - Y{user.year}</span>
                        )}
                        {user.role === "coordinator" && user.assigned_course_id && (
                          <span className="text-blue-600">
                            Assigns: {courses.find(c => c.id === user.assigned_course_id)?.name} - Y{user.assigned_year}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {["coordinator", "principal", "vice_principal"].includes(user.role) && (
                          <Switch
                            checked={user.is_available !== false}
                            onCheckedChange={() => handleAvailabilityToggle(user.id, user.is_available !== false)}
                            data-testid={`availability-toggle-${user.id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditUser(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => { setResetPwUser(user); setResetPwValue(""); }}
                            data-testid={`reset-pw-${user.id}`}
                            title="Reset Password"
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteUser(user.id)}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Requests Tab */}
        <TabsContent value="requests">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>All Gate Pass Requests</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request No.</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Course/Year</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Exit Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.slice(0, 50).map((req) => (
                    <TableRow key={req.id}>
                      <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                      <TableCell>{req.student_name}</TableCell>
                      <TableCell>{req.course_name} - Y{req.year}</TableCell>
                      <TableCell className="capitalize">{req.pass_type?.replace("_", " ")}</TableCell>
                      <TableCell>{formatDate(req.exit_date)}</TableCell>
                      <TableCell><StatusBadge status={req.status} /></TableCell>
                      <TableCell>{formatDate(req.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {req.status === "pending_principal" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                              onClick={() => handleForwardToVP(req.id, req.request_number)}
                              data-testid={`forward-vp-${req.id}`}
                            >
                              <ArrowRight className="h-3 w-3 mr-1" /> Forward to VP
                            </Button>
                          )}
                          {(req.status === "pending_coordinator" || req.status === "pending_vp_as_coordinator") && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
                                onClick={() => handleForwardCoordinatorPass(req.id, req.request_number, "principal")}
                                data-testid={`forward-principal-${req.id}`}
                              >
                                <ArrowRight className="h-3 w-3 mr-1" /> To Principal
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-purple-700 border-purple-300 hover:bg-purple-50"
                                onClick={() => handleForwardCoordinatorPass(req.id, req.request_number, "vp")}
                                data-testid={`forward-vp-coord-${req.id}`}
                              >
                                <ArrowRight className="h-3 w-3 mr-1" /> To VP
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <NotificationSettings />
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          <ReportsModule />
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Audit Trail</CardTitle>
              <CardDescription>System activity log</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Timestamp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Badge variant="outline">{log.action}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-500">{log.entity_type}</span>
                        <span className="font-mono text-xs ml-2">{log.entity_id?.slice(0, 8)}...</span>
                      </TableCell>
                      <TableCell>{log.user_name}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {JSON.stringify(log.details)}
                      </TableCell>
                      <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Edit User" : "Create New User"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}
                  placeholder="Full name"
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                  placeholder="email@example.com"
                  data-testid="user-email-input"
                />
              </div>
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                  placeholder="Enter password"
                  data-testid="user-password-input"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select 
                  value={userFormData.role} 
                  onValueChange={(v) => setUserFormData({...userFormData, role: v})}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mobile Number <span className="text-red-500">*</span></Label>
                <Input
                  value={userFormData.phone}
                  onChange={(e) => setUserFormData({...userFormData, phone: e.target.value})}
                  placeholder="Mobile number (for SMS/WhatsApp notifications)"
                  data-testid="user-phone-input"
                />
                {!userFormData.phone && userFormData.role !== "student" && userFormData.role !== "admin" && (
                  <p className="text-[10px] text-amber-600">SMS/WhatsApp notifications require a mobile number</p>
                )}
              </div>
            </div>

            {/* Student-specific fields */}
            {userFormData.role === "student" && (
              <>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-4">Student Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Enrollment Number</Label>
                      <Input
                        value={userFormData.enrollment_number}
                        onChange={(e) => setUserFormData({...userFormData, enrollment_number: e.target.value})}
                        placeholder="e.g., 2024/GNM/001"
                        data-testid="enrollment-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Course</Label>
                      <Select 
                        value={userFormData.course_id} 
                        onValueChange={(v) => setUserFormData({...userFormData, course_id: v})}
                      >
                        <SelectTrigger data-testid="course-select">
                          <SelectValue placeholder="Select course" />
                        </SelectTrigger>
                        <SelectContent>
                          {courses.map(course => (
                            <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Select 
                        value={userFormData.year} 
                        onValueChange={(v) => setUserFormData({...userFormData, year: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select year" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4].map(y => (
                            <SelectItem key={y} value={y.toString()}>Year {y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Hostel Status</Label>
                      <Select 
                        value={userFormData.hostel_status} 
                        onValueChange={(v) => setUserFormData({...userFormData, hostel_status: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hostel">Hostel</SelectItem>
                          <SelectItem value="day_scholar">Day Scholar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Parent/Guardian Name</Label>
                      <Input
                        value={userFormData.parent_name}
                        onChange={(e) => setUserFormData({...userFormData, parent_name: e.target.value})}
                        placeholder="Parent name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Parent/Guardian Phone</Label>
                      <Input
                        value={userFormData.parent_phone}
                        onChange={(e) => setUserFormData({...userFormData, parent_phone: e.target.value})}
                        placeholder="Parent phone"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Coordinator-specific fields */}
            {userFormData.role === "coordinator" && (
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-slate-700 mb-4">Coordinator Assignment</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assigned Course</Label>
                    <Select 
                      value={userFormData.assigned_course_id} 
                      onValueChange={(v) => setUserFormData({...userFormData, assigned_course_id: v})}
                    >
                      <SelectTrigger data-testid="assigned-course-select">
                        <SelectValue placeholder="Select course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map(course => (
                          <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Assigned Year</Label>
                    <Select 
                      value={userFormData.assigned_year} 
                      onValueChange={(v) => setUserFormData({...userFormData, assigned_year: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4].map(y => (
                          <SelectItem key={y} value={y.toString()}>Year {y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateUser}
              disabled={submitting}
              data-testid="save-user-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : editingUser ? "Update User" : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPwUser} onOpenChange={() => setResetPwUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          {resetPwUser && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-semibold">{resetPwUser.name}</p>
                <p className="text-slate-500">{resetPwUser.email} ({resetPwUser.role})</p>
              </div>
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input
                  type="text"
                  value={resetPwValue}
                  onChange={(e) => setResetPwValue(e.target.value)}
                  placeholder="Enter new password"
                  data-testid="admin-reset-pw-input"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleResetPassword} disabled={resetPwLoading} data-testid="admin-reset-pw-btn">
                  {resetPwLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <KeyRound className="h-4 w-4 mr-1" />}
                  Reset Password
                </Button>
                <Button variant="outline" onClick={() => setResetPwUser(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
